import { useCallback, useEffect, useState } from "react";

/** Legacy in-app squad notifications (localStorage). Superseded by DB notifications for platform events. */
export type SquadNotificationType =
  | "squad_invite"
  | "squad_proposal"
  | "squad_confirmed"
  | "squad_message"
  | "squad_reminder"
  | "squad_rejoin_request";

export interface SquadAppNotification {
  id: string;
  type: SquadNotificationType;
  title: string;
  body: string;
  createdAt: number;
  read: boolean;
  squadId?: string;
  mockPush?: boolean;
}

const KEY = "pulse.notifications.squad";

function readAll(): SquadAppNotification[] {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) return JSON.parse(raw);
  } catch {}
  return [];
}

function writeAll(items: SquadAppNotification[]) {
  try {
    localStorage.setItem(KEY, JSON.stringify(items.slice(0, 50)));
  } catch {}
  window.dispatchEvent(new CustomEvent("pulse:squad-notifications"));
}

export function pushNotification(
  n: Omit<SquadAppNotification, "id" | "createdAt" | "read"> & { mockPush?: boolean },
) {
  const items = readAll();
  const note: SquadAppNotification = {
    ...n,
    id: `n-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    createdAt: Date.now(),
    read: false,
    mockPush: n.mockPush ?? true,
  };
  writeAll([note, ...items]);
  return note;
}

export function markNotificationRead(id: string) {
  writeAll(readAll().map((n) => (n.id === id ? { ...n, read: true } : n)));
}

export function markAllNotificationsRead() {
  writeAll(readAll().map((n) => ({ ...n, read: true })));
}

export function purgeSquadNotifications(squadId: string, nightIds: string[] = []) {
  const ids = new Set([squadId, ...nightIds]);
  writeAll(readAll().filter((n) => !n.squadId || !ids.has(n.squadId)));
}

export function mockPushLabel(type: SquadNotificationType): string {
  switch (type) {
    case "squad_invite":
      return "Push · New squad invitation";
    case "squad_proposal":
      return "Push · New event proposal";
    case "squad_confirmed":
      return "Push · Event confirmed";
    case "squad_message":
      return "Push · New chat message";
    case "squad_reminder":
      return "Push · Squad starts in 2h";
    default:
      return "Push notification";
  }
}

export function useSquadNotifications() {
  const [items, setItems] = useState<SquadAppNotification[]>([]);
  const [hydrated, setHydrated] = useState(false);

  const refresh = useCallback(() => setItems(readAll()), []);

  useEffect(() => {
    refresh();
    setHydrated(true);
    const h = () => refresh();
    window.addEventListener("pulse:squad-notifications", h);
    window.addEventListener("storage", h);
    return () => {
      window.removeEventListener("pulse:squad-notifications", h);
      window.removeEventListener("storage", h);
    };
  }, [refresh]);

  const unread = items.filter((n) => !n.read).length;

  return {
    items,
    unread,
    hydrated,
    refresh,
    markRead: markNotificationRead,
    markAllRead: markAllNotificationsRead,
    push: pushNotification,
  };
}
